/**
 * GTM DataLayer Utility for PillPoint UK
 * Handles all Google Tag Manager event tracking
 */

// Ensure dataLayer exists
const getDataLayer = () => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }
  return [];
};

/**
 * Push event to GTM dataLayer
 * @param {string} eventName - The event name
 * @param {object} eventData - Additional event data
 */
export const pushToDataLayer = (eventName, eventData = {}) => {
  const dataLayer = getDataLayer();
  dataLayer.push({
    event: eventName,
    ...eventData
  });
};

/**
 * Track page view
 * @param {string} pagePath - The page path
 * @param {string} pageTitle - The page title
 */
export const trackPageView = (pagePath, pageTitle) => {
  pushToDataLayer('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    page_location: typeof window !== 'undefined' ? window.location.href : ''
  });
};

/**
 * Track product view (view_item)
 * @param {object} product - The product object
 */
export const trackViewItem = (product) => {
  if (!product) return;
  
  const price = parseFloat(String(product.price || product.priceRange?.min || 0).replace(/[^0-9.]/g, ''));
  
  pushToDataLayer('view_item', {
    ecommerce: {
      currency: product.currency || 'EUR',
      value: price,
      items: [{
        item_id: product.id || product.productId,
        item_name: product.name,
        item_brand: product.manufacturer || product.brands?.[0] || 'PillPoint',
        item_category: product.category || product.categories?.[0]?.name || 'Medicines',
        price: price,
        quantity: 1
      }]
    }
  });
};

/**
 * Track add to cart
 * @param {object} product - The product object
 * @param {number} quantity - Quantity added
 */
export const trackAddToCart = (product, quantity = 1) => {
  if (!product) return;
  
  const price = parseFloat(String(product.price || product.priceRange?.min || 0).replace(/[^0-9.]/g, ''));
  
  pushToDataLayer('add_to_cart', {
    ecommerce: {
      currency: product.currency || 'EUR',
      value: price * quantity,
      items: [{
        item_id: product.id || product.productId,
        item_name: product.name,
        item_brand: product.manufacturer || product.brands?.[0] || 'PillPoint',
        item_category: product.category || product.categories?.[0]?.name || 'Medicines',
        price: price,
        quantity: quantity
      }]
    }
  });
};

/**
 * Track remove from cart
 * @param {object} product - The product object
 * @param {number} quantity - Quantity removed
 */
export const trackRemoveFromCart = (product, quantity = 1) => {
  if (!product) return;
  
  const price = parseFloat(String(product.price || product.priceRange?.min || 0).replace(/[^0-9.]/g, ''));
  
  pushToDataLayer('remove_from_cart', {
    ecommerce: {
      currency: product.currency || 'EUR',
      value: price * quantity,
      items: [{
        item_id: product.id || product.productId,
        item_name: product.name,
        item_brand: product.manufacturer || product.brands?.[0] || 'PillPoint',
        item_category: product.category || product.categories?.[0]?.name || 'Medicines',
        price: price,
        quantity: quantity
      }]
    }
  });
};

/**
 * Track view cart
 * @param {array} items - Cart items
 * @param {number} total - Cart total
 * @param {string} currency - Currency code
 */
export const trackViewCart = (items, total, currency = 'EUR') => {
  const ecommerceItems = items.map(item => {
    const price = parseFloat(String(item.price || 0).replace(/[^0-9.]/g, ''));
    return {
      item_id: item.id || item.productId,
      item_name: item.name,
      item_brand: item.manufacturer || 'PillPoint',
      item_category: item.category || 'Medicines',
      price: price,
      quantity: item.quantity || 1
    };
  });

  pushToDataLayer('view_cart', {
    ecommerce: {
      currency: currency,
      value: total,
      items: ecommerceItems
    }
  });
};

/**
 * Track begin checkout
 * @param {array} items - Cart items
 * @param {number} total - Cart total
 * @param {string} currency - Currency code
 */
export const trackBeginCheckout = (items, total, currency = 'EUR') => {
  const ecommerceItems = items.map(item => {
    const price = parseFloat(String(item.price || 0).replace(/[^0-9.]/g, ''));
    return {
      item_id: item.id || item.productId,
      item_name: item.name,
      item_brand: item.manufacturer || 'PillPoint',
      item_category: item.category || 'Medicines',
      price: price,
      quantity: item.quantity || 1
    };
  });

  pushToDataLayer('begin_checkout', {
    ecommerce: {
      currency: currency,
      value: total,
      items: ecommerceItems
    }
  });
};

/**
 * Track purchase/conversion
 * @param {object} orderData - Order details
 */
export const trackPurchase = (orderData) => {
  const { orderId, items, total, shipping, currency = 'EUR' } = orderData;
  
  const ecommerceItems = items.map(item => {
    const price = parseFloat(String(item.price || 0).replace(/[^0-9.]/g, ''));
    return {
      item_id: item.id || item.productId,
      item_name: item.name,
      item_brand: item.manufacturer || 'PillPoint',
      item_category: item.category || 'Medicines',
      price: price,
      quantity: item.quantity || 1
    };
  });

  pushToDataLayer('purchase', {
    ecommerce: {
      transaction_id: orderId,
      currency: currency,
      value: parseFloat(total),
      shipping: parseFloat(shipping) || 0,
      tax: 0,
      items: ecommerceItems
    }
  });
};

/**
 * Track search
 * @param {string} searchTerm - The search query
 */
export const trackSearch = (searchTerm) => {
  pushToDataLayer('search', {
    search_term: searchTerm
  });
};

/**
 * Track form submission
 * @param {string} formName - Name of the form
 */
export const trackFormSubmission = (formName) => {
  pushToDataLayer('form_submission', {
    form_name: formName
  });
};

/**
 * Track button/link click
 * @param {string} elementName - Name/description of the element
 * @param {string} elementType - Type of element (button, link, etc.)
 */
export const trackClick = (elementName, elementType = 'button') => {
  pushToDataLayer('click', {
    element_name: elementName,
    element_type: elementType
  });
};

/**
 * Track scroll depth
 * @param {number} percentage - Scroll percentage (25, 50, 75, 100)
 */
export const trackScrollDepth = (percentage) => {
  pushToDataLayer('scroll', {
    scroll_depth: percentage
  });
};

